import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button, Form, Input, Alert, Spin } from "antd";
const { Password } = Input;
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CustomizeRequiredMark } from "~/components/common/custom-required-mark";
import { useNotification } from "~/hooks";
import { baseService } from "~/services/base";
import { inviteCodesService } from "~/services/invite-codes";
import { CheckCircleOutlined } from "@ant-design/icons";

type RegisterFields = {
  inviteCode: string;
  organizationName: string;
  slug: string;
  username: string;
  password: string;
  firstName: string;
  lastName: string;
  email: string;
};

export const RegisterOrgView = () => {
  const navigate = useNavigate();
  const api = useNotification();
  const [searchParams] = useSearchParams();
  const inviteCodeFromUrl = searchParams.get("inviteCode") || "";

  const [codeValidated, setCodeValidated] = useState(false);
  const [codeValidating, setCodeValidating] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (inviteCodeFromUrl) {
      form.setFieldsValue({ inviteCode: inviteCodeFromUrl });
      handleValidateCode(inviteCodeFromUrl);
    }
  }, [inviteCodeFromUrl]);

  const handleValidateCode = async (code: string) => {
    if (!code || code.length < 6) return;
    setCodeValidating(true);
    setCodeError(null);
    setCodeValidated(false);
    try {
      await inviteCodesService.validate(code);
      setCodeValidated(true);
    } catch (error: any) {
      setCodeError(
        error.response?.data?.message || "Invalid invite code"
      );
    } finally {
      setCodeValidating(false);
    }
  };

  const { mutate, isPending } = useMutation({
    mutationFn: async (values: RegisterFields) => {
      const { data } = await baseService.post("/organizations/register", values);
      return data;
    },
    onSuccess: () => {
      api.success({
        message: "Organization created",
        description: "You can now log in with your credentials.",
      });
      navigate("/login");
    },
    onError: (error: any) => {
      api.error({
        message: "Registration failed",
        description: error.response?.data?.message || "Something went wrong",
      });
    },
  });

  const onFinish = (values: RegisterFields) => {
    mutate(values);
  };

  const labelStyle = "text-xs font-bold uppercase tracking-wide text-black";
  const inputStyle =
    "border-2 border-black px-3 py-2 font-bold text-sm bg-[#faf8f3] text-black placeholder:text-black/50 focus:border-black focus:ring-0";

  return (
    <main className="flex-1 flex flex-col items-center justify-center px-4 py-12">
      <div className="max-w-6xl mx-auto flex flex-col items-center">
        <div className="mb-8 flex items-center justify-center gap-3">
          <h1
            className="text-4xl sm:text-5xl text-black"
            style={{
              fontFamily: "'Oswald', sans-serif",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
            }}
          >
            Register Your Company
          </h1>
        </div>

        <p
          className="text-sm text-center text-black/70 mb-8 max-w-md"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          Enter your invite code to register your art handling company on the platform.
        </p>

        <div className="w-full max-w-lg">
          <div className="border-2 border-black bg-[#faf8f3] p-8 sm:p-10">
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              requiredMark={CustomizeRequiredMark}
            >
              <div className="border-b-2 border-black pb-4 mb-4">
                <span
                  className="text-xs font-bold uppercase tracking-widest text-black/50"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  Invite Code
                </span>
              </div>

              <Form.Item
                name="inviteCode"
                label={<span className={labelStyle} style={{ fontFamily: "'Montserrat', sans-serif" }}>Invite Code</span>}
                rules={[{ required: true, message: "An invite code is required to register" }]}
                extra={
                  codeValidating ? (
                    <span className="text-xs text-black/50"><Spin size="small" /> Validating...</span>
                  ) : codeValidated ? (
                    <span className="text-xs text-green-600"><CheckCircleOutlined /> Valid invite code</span>
                  ) : null
                }
                validateStatus={codeError ? "error" : codeValidated ? "success" : undefined}
                help={codeError || undefined}
              >
                <Input
                  size="large"
                  placeholder="Enter your invite code"
                  className={inputStyle}
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                  onBlur={(e) => handleValidateCode(e.target.value)}
                  onChange={() => {
                    setCodeValidated(false);
                    setCodeError(null);
                  }}
                />
              </Form.Item>

              {!codeValidated && !inviteCodeFromUrl && (
                <Alert
                  message="Registration is invite-only"
                  description="You need a valid invite code to register. Contact us if you need one."
                  type="info"
                  showIcon
                  className="mb-6"
                />
              )}

              <div className="border-b-2 border-black pb-4 mb-4 mt-4">
                <span
                  className="text-xs font-bold uppercase tracking-widest text-black/50"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  Company Info
                </span>
              </div>

              <Form.Item
                name="organizationName"
                label={<span className={labelStyle} style={{ fontFamily: "'Montserrat', sans-serif" }}>Company Name</span>}
                rules={[{ required: true, message: "Required" }]}
              >
                <Input
                  size="large"
                  placeholder="e.g. Brooklyn Art Movers"
                  className={inputStyle}
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                />
              </Form.Item>

              <Form.Item
                name="slug"
                label={<span className={labelStyle} style={{ fontFamily: "'Montserrat', sans-serif" }}>URL Slug</span>}
                rules={[
                  { required: true, message: "Required" },
                  { pattern: /^[a-z0-9-]+$/, message: "Lowercase letters, numbers, and hyphens only" },
                ]}
                extra={<span className="text-xs text-black/50">Used in URLs. e.g. "brooklyn-art-movers"</span>}
              >
                <Input
                  size="large"
                  placeholder="brooklyn-art-movers"
                  className={inputStyle}
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                />
              </Form.Item>

              <div className="border-b-2 border-black pb-4 mb-4 mt-8">
                <span
                  className="text-xs font-bold uppercase tracking-widest text-black/50"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  Admin Account
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Form.Item
                  name="firstName"
                  label={<span className={labelStyle} style={{ fontFamily: "'Montserrat', sans-serif" }}>First Name</span>}
                  rules={[{ required: true, message: "Required" }]}
                >
                  <Input size="large" className={inputStyle} style={{ fontFamily: "'Montserrat', sans-serif" }} />
                </Form.Item>
                <Form.Item
                  name="lastName"
                  label={<span className={labelStyle} style={{ fontFamily: "'Montserrat', sans-serif" }}>Last Name</span>}
                  rules={[{ required: true, message: "Required" }]}
                >
                  <Input size="large" className={inputStyle} style={{ fontFamily: "'Montserrat', sans-serif" }} />
                </Form.Item>
              </div>

              <Form.Item
                name="email"
                label={<span className={labelStyle} style={{ fontFamily: "'Montserrat', sans-serif" }}>Email</span>}
                rules={[
                  { required: true, message: "Required" },
                  { type: "email", message: "Invalid email" },
                ]}
              >
                <Input size="large" placeholder="admin@yourcompany.com" className={inputStyle} style={{ fontFamily: "'Montserrat', sans-serif" }} />
              </Form.Item>

              <Form.Item
                name="username"
                label={<span className={labelStyle} style={{ fontFamily: "'Montserrat', sans-serif" }}>Username</span>}
                rules={[{ required: true, message: "Required" }]}
              >
                <Input size="large" className={inputStyle} style={{ fontFamily: "'Montserrat', sans-serif" }} />
              </Form.Item>

              <Form.Item
                name="password"
                label={<span className={labelStyle} style={{ fontFamily: "'Montserrat', sans-serif" }}>Password</span>}
                rules={[
                  { required: true, message: "Required" },
                  { min: 8, message: "Minimum 8 characters" },
                ]}
              >
                <Password size="large" className={inputStyle} style={{ fontFamily: "'Montserrat', sans-serif" }} />
              </Form.Item>

              <Button
                loading={isPending}
                htmlType="submit"
                size="large"
                className="w-full border-2 border-black bg-black text-white px-4 py-3 font-bold text-sm uppercase tracking-wide hover:opacity-90 transition-all mt-4"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {isPending ? "Creating..." : "Create Organization"}
              </Button>

              <div
                className="text-center text-sm font-medium text-black pt-6 border-t-2 border-black mt-6"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Already have an account?{" "}
                <Link
                  to="/login"
                  className="font-bold uppercase tracking-wide text-black hover:opacity-70 transition-opacity border-b-2 border-transparent hover:border-black"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  Sign In
                </Link>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </main>
  );
};
